// client/audio.ts — Sons du jeu synthétisés à la volée (Web Audio), zéro asset, zéro
// dépendance. Un AudioContext unique est créé au PREMIER input utilisateur (les
// navigateurs interdisent le son avant un geste) : main.ts appelle unlockAudio() au
// premier keydown. Chaque événement de jeu déclenche un court jingle (oscillateurs +
// enveloppes). L'état muet est persisté dans localStorage 'mad.muted' ; la touche M
// (gérée par main.ts) le bascule, et le HUD affiche l'état courant via isMuted().

import type { GameEvent } from '../shared/state'
import { cadenceOk } from './cadence'
import { serieHz } from './serie'
import type { WorldKey } from '../shared/worlds'
import { deathSound } from './death'
import {
  MUSIC, STEPS_PER_BAR, arpAt, bassAt, padVoicing, percAt, type MusicSpec, type Wave,
} from './music'

let ctx: AudioContext | null = null

// --- Bus master + réverb (Volet B) -----------------------------------------------------------
// Tout le son (SFX + musique) transite par deux bus de gain (sfxBus, musicBus) qui alimentent
// À LA FOIS la destination (voie SÈCHE, 100 %) et un ConvolverNode partagé via des envois (voie
// MOUILLÉE, dosage léger) -> réverb subtile sans noyer les SFX secs déjà réglés. L'impulse
// response est GÉNÉRÉE en code (bruit à décroissance expo, ~1.2 s) : zéro asset externe.
let sfxBus: GainNode | null = null    // les SFX s'y connectent (gain 1, ils se gardent eux-mêmes du mute)
let musicBus: GainNode | null = null  // la musique s'y connecte ; son gain = volume musique / fondu / mute
let musicSend: GainNode | null = null // envoi réverb de la musique (dosé par biome au démarrage)

// Génère une impulse response de réverb : bruit stéréo à décroissance exponentielle. Une seule
// fois (mise en cache dans le convolver). `seconds` = longueur de la queue, `decay` = raideur.
function makeImpulse(c: AudioContext, seconds: number, decay: number): AudioBuffer {
  const rate = c.sampleRate
  const len = Math.max(1, Math.floor(rate * seconds))
  const buf = c.createBuffer(2, len, rate)
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch)
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay)
  }
  return buf
}

// Construit le graphe de bus UNE fois, sur le contexte fraîchement créé. Idempotent via le test
// d'existence dans ac(). SFX -> sfxBus -> destination (sec) + sfxBus -> envoi -> convolver (mouillé).
// musicBus démarre à 0 (silence) : seul startMusic le monte.
function buildGraph(c: AudioContext): void {
  const conv = c.createConvolver()
  conv.buffer = makeImpulse(c, 1.2, 2.6)
  conv.connect(c.destination)               // sortie mouillée commune
  sfxBus = c.createGain(); sfxBus.gain.value = 1
  sfxBus.connect(c.destination)             // SFX secs (punch préservé)
  const sfxSend = c.createGain(); sfxSend.gain.value = 0.14 // réverb SFX subtile (~14 %)
  sfxBus.connect(sfxSend).connect(conv)
  musicBus = c.createGain(); musicBus.gain.value = 0        // muet tant que startMusic n'a pas monté le fondu
  musicBus.connect(c.destination)           // musique sèche
  musicSend = c.createGain(); musicSend.gain.value = 0.25   // réglé par biome au démarrage (espace)
  musicBus.connect(musicSend).connect(conv)
}

// localStorage peut lever (SecurityError) sur les profils « bloquer tous les cookies » :
// tout accès est donc gardé. En cas d'échec : pas de persistance, muet reste à false.
function safeGet(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function safeSet(key: string, val: string): void {
  try { localStorage.setItem(key, val) } catch { /* profil sans stockage : on n'écrit rien */ }
}

let muted = safeGet('mad.muted') === '1'

// AudioContext paresseux : créé au premier usage, réveillé s'il est suspendu (politique
// autoplay des navigateurs). Appelé depuis un geste utilisateur (unlockAudio) ou un jingle.
function ac(): AudioContext {
  if (!ctx) { ctx = new AudioContext(); buildGraph(ctx) } // graphe de bus construit une fois avec le contexte
  if (ctx.state === 'suspended') void ctx.resume()
  return ctx
}

// Débloque le son sur un vrai geste (premier keydown / pointerdown). Sur iOS, resume() SEUL ne
// suffit pas : on joue AUSSI un buffer SILENCIEUX d'une frame DANS le geste pour amorcer le
// contexte, et (une seule fois) on arme sa reprise au retour d'onglet — iOS suspend l'AudioContext
// au verrouillage de l'écran ou au changement d'app sans jamais le relancer de lui-même. Le buffer
// muet est inaudible et sans effet sur desktop ; les réveils ne font que resume() si besoin.
// Idempotent. (⚠️ Ce fix ne contourne PAS l'interrupteur SILENCIEUX physique d'un iPhone, limite
// dure du Web Audio : téléphone en mode silencieux = pas de son, quel que soit le code.)
let wakeArmed = false
export function unlockAudio(): void {
  const a = ac()
  try {
    const src = a.createBufferSource()
    src.buffer = a.createBuffer(1, 1, a.sampleRate)
    src.connect(a.destination); src.start(0)
  } catch { /* refus avant interaction sur certains navigateurs : le resume() de ac() suffit alors */ }
  if (wakeArmed) return
  wakeArmed = true
  const wake = (): void => { if (ctx && ctx.state !== 'running') void ctx.resume() }
  document.addEventListener('visibilitychange', () => { if (!document.hidden) wake() })
  addEventListener('focus', wake)
  addEventListener('pointerdown', wake) // tout geste ultérieur relance le son si iOS l'a re-suspendu entre-temps
}

export function isMuted(): boolean { return muted }

export function toggleMute(): boolean {
  muted = !muted
  safeSet('mad.muted', muted ? '1' : '0')
  syncMusicMute() // le mute coupe/rétablit AUSSI la musique en cours (le bus musique suit)
  return muted
}

// Brique de base : un oscillateur + enveloppe expo descendante, démarré à `when`.
function blip(when: number, freq: number, ms: number, type: OscillatorType, to: number | undefined, vol: number): void {
  const a = ac(), o = a.createOscillator(), g = a.createGain()
  o.type = type
  o.frequency.setValueAtTime(freq, when)
  if (to) o.frequency.exponentialRampToValueAtTime(to, when + ms / 1000)
  g.gain.setValueAtTime(vol, when)
  g.gain.exponentialRampToValueAtTime(0.001, when + ms / 1000)
  o.connect(g).connect(sfxBus!) // -> bus master SFX (voie sèche + envoi réverb)
  o.start(when); o.stop(when + ms / 1000)
}

// Note unique jouée immédiatement (signature du squelette du brief).
function tone(freq: number, ms: number, type: OscillatorType = 'square', to?: number, vol = 0.15): void {
  if (muted) return
  blip(ac().currentTime, freq, ms, type, to, vol)
}

// Ping cristallin à l'APEX du rebond « propulseur » (18/07, variantes du saut trampoline) :
// programmé À L'AVANCE sur l'horloge audio (delaySec = durée de la montée) -> il tombe pile au
// sommet même si l'affichage rame. Appelé par solo/online sur l'événement bounce (variante 3).
export function apexPing(delaySec: number): void {
  if (muted) return
  blip(ac().currentTime + delaySec, 1400, 90, 'sine', undefined, 0.07)
}

// --- Cinématique de casse de la Mine (Lot C, T4) : sons de frappe, forage et fanfares de
// révélation. Ajoutés ICI car les primitives tone/noise/seq/blip sont privées au module. Tous
// respectent `muted`. Purement cosmétiques (hors simulation / hash / rejeu). ---

// Frappe de pioche sur la brique. `heavy` = marteau doré (impact plus sourd et fort + sub grave) ;
// `final` = la frappe qui BRISE la brique (plus longue, avec un éclat clair de rupture par-dessus).
export function crackStrike(heavy: boolean, final = false): void {
  if (muted) return
  const vol = (heavy ? 0.22 : 0.16) * (final ? 1.3 : 1)
  tone(heavy ? 105 : 150, final ? 130 : 70, 'square', heavy ? 62 : 90, vol) // « tchac » (grave si marteau)
  noise(final ? 130 : 70, heavy ? 900 : 1700, heavy ? 0.2 : 0.13)           // gravats qui volent
  if (heavy || final) tone(58, final ? 220 : 130, 'sine', 40, final ? 0.3 : 0.2) // sub d'impact
  if (final) tone(1400, 160, 'triangle', 2200, 0.12)                        // éclat clair de rupture
}

// Vrombissement CONTINU de la foreuse (variante de pioche) : un bourdon dense programmé À L'AVANCE
// sur l'horloge audio pour `durationSec` (robuste aux images sautées, comme apexPing). Deux
// sawtooth légèrement désaccordés -> battement « moteur qui fore » ; enveloppe attaque/tenue/chute.
// Se coupe tout seul à la fin (rien à arrêter côté appelant).
export function crackDrill(durationSec: number): void {
  if (muted) return
  const a = ac(), t = a.currentTime, dur = Math.max(0.3, durationSec)
  const g = a.createGain()
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(0.1, t + 0.1)        // attaque courte
  g.gain.setValueAtTime(0.1, Math.max(t + 0.1, t + dur - 0.15))
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur)     // extinction
  g.connect(sfxBus!) // -> bus master SFX
  const o1 = a.createOscillator(), o2 = a.createOscillator()
  o1.type = 'sawtooth'; o1.frequency.setValueAtTime(96, t)
  o2.type = 'sawtooth'; o2.frequency.setValueAtTime(98.5, t) // léger désaccord -> vrombissement
  o1.connect(g); o2.connect(g)
  o1.start(t); o2.start(t); o1.stop(t + dur); o2.stop(t + dur)
}

// Fanfare de RÉVÉLATION selon la rareté : courant = bip doux ; rare = petit arpège montant ;
// épique = fanfare 3 notes affirmée ; méga = fanfare longue + scintillement cristallin.
export function crackFanfare(rarity: 'courant' | 'rare' | 'epique' | 'mega'): void {
  if (muted) return
  switch (rarity) {
    case 'courant':
      seq([{ f: 660, ms: 130, at: 0, type: 'sine', vol: 0.12 }]) // bip doux unique
      break
    case 'rare':
      seq([
        { f: 523, ms: 90, at: 0, type: 'triangle' },
        { f: 659, ms: 90, at: 0.09, type: 'triangle' },
        { f: 880, ms: 160, at: 0.18, type: 'triangle' },
      ])
      break
    case 'epique':
      seq([
        { f: 659, ms: 120, at: 0, type: 'square', vol: 0.16 },
        { f: 880, ms: 120, at: 0.12, type: 'square', vol: 0.16 },
        { f: 1175, ms: 260, at: 0.24, type: 'square', vol: 0.16 },
      ])
      break
    case 'mega':
      seq([
        { f: 523, ms: 110, at: 0, type: 'triangle', vol: 0.16 },
        { f: 659, ms: 110, at: 0.1, type: 'triangle', vol: 0.16 },
        { f: 784, ms: 110, at: 0.2, type: 'triangle', vol: 0.16 },
        { f: 1047, ms: 140, at: 0.3, type: 'triangle', vol: 0.16 },
        { f: 1319, ms: 320, at: 0.44, type: 'triangle', vol: 0.16 },
        { f: 2093, ms: 90, at: 0.62, type: 'sine', vol: 0.08 }, // scintillement final
        { f: 2637, ms: 90, at: 0.72, type: 'sine', vol: 0.07 },
        { f: 3136, ms: 150, at: 0.82, type: 'sine', vol: 0.06 },
      ])
      break
  }
}

// Pas du coureur (Lot C T5, loot `steps`) : son TRÈS discret joué au changement de case en solo.
// `pas-metal` = petit « tic » métallique clair ; `pas-caverne` = pas sourd qui résonne (deux échos
// doux). loot null / inconnu : rien. Volumes bas (un pas se devine, il ne couvre pas le jeu). Passe
// par tone/noise/seq -> respecte automatiquement le muet.
// Vague 2 (21/07) : 5 nouveaux timbres de pas (feutrés/cliquetants/aquatiques/cristallins/fantômes),
// chacun sa recette de synthé, tous au même niveau discret que les deux d'origine.
export function stepSound(lootId: string | null): void {
  if (muted || !lootId) return
  if (lootId === 'pas-metal') { tone(2100, 22, 'square', 1500, 0.035); noise(24, 5200, 0.03) } // « tic » métallique
  else if (lootId === 'pas-caverne') {
    seq([
      { f: 320, ms: 45, at: 0, type: 'sine', to: 220, vol: 0.045 },
      { f: 240, ms: 70, at: 0.07, type: 'sine', to: 170, vol: 0.022 }, // écho de caverne
    ])
  }
  // --- Vague 2 ---
  else if (lootId === 'pas-feutres') { noise(40, 240, 0.028) } // ouaté : bruit grave très court, aucune tonalité
  else if (lootId === 'pas-cliquetis') {
    seq([
      { f: 2600, ms: 12, at: 0, type: 'square', to: 2000, vol: 0.03 },
      { f: 3200, ms: 10, at: 0.03, type: 'square', to: 2600, vol: 0.022 }, // petit double-clic sec
    ])
  }
  else if (lootId === 'pas-aquatiques') { tone(560, 55, 'sine', 190, 0.05); noise(26, 360, 0.025) } // ploc mouillé qui descend
  else if (lootId === 'pas-cristallins') {
    seq([
      { f: 2400, ms: 35, at: 0, type: 'sine', to: 3000, vol: 0.04 },
      { f: 3600, ms: 45, at: 0.03, type: 'sine', vol: 0.018 }, // tintement de cristal (harmonique aiguë)
    ])
  }
  else if (lootId === 'pas-fantomes') {
    seq([
      { f: 380, ms: 120, at: 0, type: 'sine', to: 170, vol: 0.04 },
      { f: 280, ms: 150, at: 0.09, type: 'sine', to: 120, vol: 0.02 }, // souffle spectral qui descend en écho
    ])
  }
}

// Petits cris des COMPAGNONS (21/07 soir) : joués par client/pet.ts au DÉBUT d'une action spéciale
// d'oisiveté (le planning interne espace déjà de ~10-20 s ; garde-fou de cadence en plus ici).
// Volumes très bas : un familier se devine, il ne couvre jamais le jeu. Muet respecté via tone/noise/seq.
let lastPetCue = 0
export function petCue(petId: string, kind: 'special' | 'joy' | 'alert' | 'cuddle' = 'special'): void {
  if (muted) return
  const now = performance.now()
  if (now - lastPetCue < 4000) return // jamais deux cris à moins de 4 s (multi : plusieurs pets)
  lastPetCue = now
  if (kind === 'joy') { // petit gazouillis de joie, aigu chez les petits, plus rond chez les gros
    const base = petId === 'pet-loup' ? 520 : petId === 'pet-chat' || petId === 'pet-renard' ? 780 : 1400
    seq([
      { f: base, ms: 55, at: 0, type: 'sine', to: base * 1.35, vol: 0.026 },
      { f: base * 1.3, ms: 60, at: 0.07, type: 'sine', to: base * 1.6, vol: 0.02 },
    ])
    return
  }
  if (kind === 'alert') { // grognement sourd (loup) / feulement (chat)
    if (petId === 'pet-loup') { tone(120, 260, 'sawtooth', 78, 0.03); noise(140, 420, 0.02) }
    else { noise(160, 5200, 0.026); tone(640, 90, 'sine', 520, 0.018) }
    return
  }
  if (kind === 'cuddle') { // ronron / roucoulade contents
    if (petId === 'pet-chat') seq([
      { f: 220, ms: 90, at: 0, type: 'sine', to: 180, vol: 0.024 },
      { f: 210, ms: 90, at: 0.11, type: 'sine', to: 175, vol: 0.022 },
      { f: 205, ms: 100, at: 0.22, type: 'sine', to: 170, vol: 0.02 },
    ])
    else seq([
      { f: 460, ms: 90, at: 0, type: 'sine', to: 560, vol: 0.024 },
      { f: 560, ms: 110, at: 0.1, type: 'sine', to: 500, vol: 0.02 },
    ])
    return
  }
  switch (petId) {
    case 'pet-loup': // hurlement miniature : glissando qui monte puis retombe
      seq([
        { f: 420, ms: 260, at: 0, type: 'sine', to: 680, vol: 0.038 },
        { f: 660, ms: 320, at: 0.24, type: 'sine', to: 360, vol: 0.03 },
      ]); break
    case 'pet-chouette': // hou-hou feutré
      seq([
        { f: 540, ms: 110, at: 0, type: 'sine', to: 470, vol: 0.04 },
        { f: 510, ms: 150, at: 0.16, type: 'sine', to: 440, vol: 0.032 },
      ]); break
    case 'pet-perroquet': // pépiement perché
      seq([
        { f: 1500, ms: 45, at: 0, type: 'square', to: 2100, vol: 0.02 },
        { f: 1900, ms: 55, at: 0.07, type: 'square', to: 1400, vol: 0.016 },
      ]); break
    case 'pet-canari': // trille clair
      seq([
        { f: 2300, ms: 40, at: 0, type: 'sine', to: 2700, vol: 0.024 },
        { f: 2600, ms: 45, at: 0.06, type: 'sine', to: 3000, vol: 0.018 },
      ]); break
    case 'pet-serpent': noise(170, 5600, 0.02); break // sss discret
    case 'pet-dragonnet': // crépitement de flammèche
      noise(90, 1500, 0.028); tone(300, 120, 'sine', 170, 0.024); break
    case 'pet-follet': // chatoiement cristallin
      seq([
        { f: 1900, ms: 70, at: 0, type: 'sine', to: 2500, vol: 0.02 },
        { f: 2700, ms: 60, at: 0.08, type: 'sine', vol: 0.014 },
      ]); break
    case 'pet-chat': // petit miaou
      seq([{ f: 880, ms: 160, at: 0, type: 'sine', to: 620, vol: 0.028 }]); break
    // renard (dodo) et araignée (silencieuse) : muets, exprès.
  }
}

// Bruit blanc (buffer aléatoire) filtré passe-bas, même enveloppe descendante.
function noise(ms: number, cutoff = 800, vol = 0.2, delay = 0): void {
  if (muted) return
  const a = ac()
  const len = Math.max(1, Math.floor(a.sampleRate * ms / 1000))
  const buf = a.createBuffer(1, len, a.sampleRate)
  const data = buf.getChannelData(0)
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1
  const src = a.createBufferSource(); src.buffer = buf
  const lp = a.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.setValueAtTime(cutoff, a.currentTime)
  const g = a.createGain()
  const t0 = a.currentTime + delay
  g.gain.setValueAtTime(vol, t0)
  g.gain.exponentialRampToValueAtTime(0.001, t0 + ms / 1000)
  src.connect(lp).connect(g).connect(sfxBus!) // -> bus master SFX
  src.start(t0); src.stop(t0 + ms / 1000)
}

// Séquence de notes espacées sur l'horloge audio (doubles blips, arpèges, fanfares).
type Note = { f: number; ms: number; at: number; type?: OscillatorType; to?: number; vol?: number }
function seq(notes: Note[]): void {
  if (muted) return
  const base = ac().currentTime
  for (const n of notes) blip(base + n.at, n.f, n.ms, n.type ?? 'square', n.to, n.vol ?? 0.15)
}

// boom : bruit blanc passe-bas 300 ms + sub grave 60 Hz (explosion de bombe).
function boom(): void {
  if (muted) return
  noise(300, 420, 0.3)
  tone(60, 300, 'sine', 40, 0.35)
}

// teleport : sweep sinus 300 -> 1200 -> 300 Hz sur 200 ms.
function teleport(): void {
  if (muted) return
  const a = ac(), o = a.createOscillator(), g = a.createGain()
  const t = a.currentTime
  o.type = 'sine'
  o.frequency.setValueAtTime(300, t)
  o.frequency.exponentialRampToValueAtTime(1200, t + 0.1)
  o.frequency.exponentialRampToValueAtTime(300, t + 0.2)
  g.gain.setValueAtTime(0.16, t)
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.2)
  o.connect(g).connect(sfxBus!) // -> bus master SFX
  o.start(t); o.stop(t + 0.2)
}

// denied : « toc » sourd et bref (~55 ms) pour un geste refusé (creuser/utiliser un
// objet là où c'est impossible). Un clic grave descendant + une pointe de bruit basse
// (texture « bois »). Volontairement terne et discret — faible volume, aucune montée :
// un refus se signale, il ne se récompense pas.
function toc(): void {
  if (muted) return
  tone(190, 55, 'sine', 90, 0.1)
  noise(35, 300, 0.06)
}

// --- Ambiance sonore de fond, PAR BIOME (synthétisée, sparse) : un son doux (goutte, vent, oiseau,
// clank, tintement, pulsation) à intervalle IRRÉGULIER tant qu'un niveau est en cours. Chaque son
// passe par tone/noise -> respecte automatiquement le muet. Démarrée/arrêtée par les contrôleurs. ---
let ambTimer: ReturnType<typeof setTimeout> | null = null
const AMB: Record<WorldKey, { base: number; jit: number; tick: () => void }> = {
  grotte: { base: 2600, jit: 2200, tick: () => tone(1400, 45, 'sine', 900, 0.05) },                                                                     // goutte d'eau
  desert: { base: 3000, jit: 2600, tick: () => noise(700, 520, 0.045) },                                                                                // rafale de vent
  usine: { base: 2300, jit: 1800, tick: () => seq([{ f: 180, ms: 70, at: 0, type: 'square', to: 120, vol: 0.05 }, { f: 90, ms: 60, at: 0.06, vol: 0.04 }]) }, // clank métallique
  prairie: { base: 2800, jit: 2400, tick: () => seq([{ f: 1900, ms: 40, at: 0, type: 'triangle', vol: 0.05 }, { f: 2300, ms: 40, at: 0.08, type: 'triangle', vol: 0.05 }, { f: 2050, ms: 55, at: 0.16, type: 'triangle', vol: 0.05 }]) }, // pépiement d'oiseau
  neige: { base: 3600, jit: 2800, tick: () => tone(2100, 45, 'sine', 3200, 0.035) },                                                                    // tintement cristallin
  classique: { base: 4000, jit: 3000, tick: () => tone(70, 200, 'sine', 55, 0.04) },                                                                    // pulsation nocturne grave
  // Abysses (16/07 nuit) : chapelet de bulles qui remonte — deux blips sinus qui MONTENT en
  // fréquence et s'espacent, très doux. C'est le son qu'on entend quand on retient son souffle.
  abysses: { base: 2500, jit: 2000, tick: () => seq([{ f: 620, ms: 55, at: 0, type: 'sine', to: 980, vol: 0.045 }, { f: 760, ms: 45, at: 0.13, type: 'sine', to: 1180, vol: 0.03 }]) },
  // Manoir hanté : craquement de parquet lointain (bruit très grave et court) — rare, il fait
  // sursauter sans jamais couvrir le jeu. Volontairement le plus espacé des huit.
  manoir: { base: 4200, jit: 3400, tick: () => { noise(120, 190, 0.05); tone(58, 260, 'sine', 44, 0.035) } },
}
/** Démarre l'ambiance du biome `world` (remplace l'ambiance en cours), sons sparse au timing irrégulier. */
export function startAmbiance(world: WorldKey): void {
  stopAmbiance()
  const spec = AMB[world]
  const loop = (): void => { spec.tick(); ambTimer = setTimeout(loop, spec.base + Math.random() * spec.jit) }
  ambTimer = setTimeout(loop, 900 + Math.random() * spec.jit) // premier son après un court délai
}
/** Arrête l'ambiance de fond (changement de niveau / sortie de partie). */
export function stopAmbiance(): void { if (ambTimer !== null) { clearTimeout(ambTimer); ambTimer = null } }

// --- Musique de fond GÉNÉRATIVE, par biome (Volet A) -----------------------------------------
// Boucle musicale douce et MODERNE (nappes désaccordées + passe-bas, basse, arpège, percu légère)
// distincte par biome, jouée tant qu'un niveau est en cours. Scheduler à LOOKAHEAD sur l'horloge
// audio (ctx.currentTime) : un petit setInterval programme ~0.12 s de notes À L'AVANCE -> pas de
// dérive ni de rafale (contrairement à un setTimeout par note). Les décisions musicales viennent
// du module PUR ./music ; ici on ne fait que les RÉALISER en Web Audio. 100 % cosmétique (hors
// sim/hash/rejeu ; Math.random() n'est utilisé QUE pour la variation d'arpège, comme l'ambiance).
const MUSIC_LEVEL = 0.55   // volume MAÎTRE de la musique (lit sonore, volontairement bien sous les SFX)
const LOOKAHEAD = 0.12     // fenêtre d'anticipation du scheduler (s)
const SCHED_MS = 25        // période de réveil du scheduler (ms)

let musicWorld: WorldKey | null = null
let musicTimer: ReturnType<typeof setInterval> | null = null
let stepIndex = 0          // pas courant (double-croche) depuis le début de la boucle
let nextTime = 0           // date (horloge audio) du prochain pas à programmer
// Toutes les sources musicales EN VOL : suivies pour un arrêt + déconnexion PROPRES (zéro fuite).
// Chaque source se retire elle-même du set et se déconnecte à sa fin (onended).
const voices = new Set<AudioScheduledSourceNode>()

// Oscillateur suivi : démarre, s'arrête à `stop`, se connecte à `dest`, se nettoie à sa fin
// (retrait du set + déconnexion). `onEnd` = nettoyage optionnel des nœuds intermédiaires partagés.
function voice(type: Wave, freq: number, when: number, stop: number, detune: number, dest: AudioNode, onEnd?: () => void): void {
  const o = ctx!.createOscillator()
  o.type = type
  o.frequency.setValueAtTime(freq, when)
  if (detune) o.detune.setValueAtTime(detune, when)
  o.connect(dest)
  voices.add(o)
  o.onended = () => { voices.delete(o); try { o.disconnect() } catch { /* déjà déconnecté */ } ; onEnd?.() }
  o.start(when); o.stop(stop)
}

// Nappe : l'accord courant, deux voix désaccordées par note, à travers un passe-bas + enveloppe
// douce (attaque/tenue/relâche), partagés par tout l'accord et déconnectés à la fin de la nappe.
function firePad(spec: MusicSpec, step: number, when: number, dur: number): void {
  const c = ctx!
  const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.setValueAtTime(spec.padCutoff, when)
  const g = c.createGain()
  g.gain.setValueAtTime(0.0001, when)
  g.gain.exponentialRampToValueAtTime(spec.padGain, when + dur * 0.35)          // attaque douce
  g.gain.setValueAtTime(spec.padGain, when + dur * 0.7)                         // tenue
  g.gain.exponentialRampToValueAtTime(0.0001, when + dur)                       // relâche
  lp.connect(g).connect(musicBus!)
  const freqs = padVoicing(spec, step)
  freqs.forEach((f, i) => {
    const cleanup = i === freqs.length - 1 ? () => { try { lp.disconnect(); g.disconnect() } catch { /* déjà nettoyé */ } } : undefined
    voice(spec.padWave, f, when, when + dur, -spec.padDetune / 2, lp)           // voix grave désaccordée
    voice(spec.padWave, f, when, when + dur, spec.padDetune / 2, lp, cleanup)   // voix aiguë désaccordée
  })
}

// Basse : une note avec attaque quasi immédiate et extinction expo. Gain déconnecté à la fin.
function fireBass(spec: MusicSpec, freq: number, when: number, dur: number): void {
  const g = ctx!.createGain()
  g.gain.setValueAtTime(0.0001, when)
  g.gain.exponentialRampToValueAtTime(spec.bassGain, when + 0.02)
  g.gain.exponentialRampToValueAtTime(0.0001, when + dur)
  g.connect(musicBus!)
  voice(spec.bassWave, freq, when, when + dur, 0, g, () => { try { g.disconnect() } catch { /* nettoyé */ } })
}

// Arpège : une note « pluck » (attaque très courte, extinction expo). Gain déconnecté à la fin.
function fireArp(spec: MusicSpec, freq: number, when: number, dur: number): void {
  const g = ctx!.createGain()
  g.gain.setValueAtTime(0.0001, when)
  g.gain.exponentialRampToValueAtTime(spec.arpGain, when + 0.008)
  g.gain.exponentialRampToValueAtTime(0.0001, when + dur)
  g.connect(musicBus!)
  voice(spec.arpWave, freq, when, when + dur, 0, g, () => { try { g.disconnect() } catch { /* nettoyé */ } })
}

// Percussion légère : court bruit passe-bande + extinction expo (tic feutré). Tout déconnecté à la fin.
function firePerc(spec: MusicSpec, when: number): void {
  const c = ctx!, ms = 55
  const len = Math.max(1, Math.floor(c.sampleRate * ms / 1000))
  const buf = c.createBuffer(1, len, c.sampleRate)
  const d = buf.getChannelData(0)
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1
  const src = c.createBufferSource(); src.buffer = buf
  const bp = c.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.setValueAtTime(spec.percCutoff, when)
  const g = c.createGain()
  g.gain.setValueAtTime(spec.percGain, when)
  g.gain.exponentialRampToValueAtTime(0.0001, when + ms / 1000)
  src.connect(bp).connect(g).connect(musicBus!)
  voices.add(src)
  src.onended = () => { voices.delete(src); try { src.disconnect(); bp.disconnect(); g.disconnect() } catch { /* nettoyé */ } }
  src.start(when); src.stop(when + ms / 1000)
}

// Programme UN pas de la grille (double-croche) sur l'horloge audio : nappe aux frontières de
// mesure (× padBars), puis basse / arpège / percu selon la partition pure du biome.
function scheduleStep(spec: MusicSpec, step: number, when: number): void {
  const stepDur = 60 / spec.bpm / 4
  const barSpan = STEPS_PER_BAR * spec.padBars
  if (step % barSpan === 0) firePad(spec, step, when, stepDur * barSpan)
  const bf = bassAt(spec, step)
  if (bf !== null) fireBass(spec, bf, when, stepDur * 3)
  const af = arpAt(spec, step, Math.random())
  if (af !== null) fireArp(spec, af, when, stepDur * 1.6)
  if (percAt(spec, step)) firePerc(spec, when)
}

// Réveil périodique du scheduler : programme tous les pas dont la date tombe dans la fenêtre
// d'anticipation. Si l'horloge nous a dépassés (onglet en arrière-plan -> setInterval throttlé),
// on RECALE nextTime sur maintenant plutôt que de rattraper (sinon rafale de notes simultanées).
function tickScheduler(): void {
  if (!ctx || musicWorld === null) return
  const spec = MUSIC[musicWorld]
  const now = ctx.currentTime
  if (nextTime < now) nextTime = now
  const stepDur = 60 / spec.bpm / 4
  const horizon = now + LOOKAHEAD
  while (nextTime < horizon) {
    if (!muted) scheduleStep(spec, stepIndex, nextTime) // muet : on avance la phase SANS créer de nœud
    stepIndex += 1
    nextTime += stepDur
  }
}

// Arrête toutes les sources musicales en vol à la date `at` ; leur onended les déconnecte et vide
// le set. Coupe même les nappes longues (le stop reprogrammé plus tôt l'emporte).
function hardStopVoices(at: number): void {
  for (const v of voices) { try { v.stop(at) } catch { /* déjà arrêtée */ } }
}

/**
 * Démarre (ou bascule vers) la musique du biome `world`. IDEMPOTENT sur le biome : rappelé avec
 * le MÊME monde (respawn, niveau suivant du même biome), il ne coupe rien -> le groove continue.
 * Changement de biome : fondu enchaîné propre (sortie de l'ancien, anciennes voix coupées, entrée
 * du nouveau). Respecte `muted` (bus à 0 si muet ; le scheduler ne crée alors aucun nœud).
 * À appeler aux MÊMES points que startAmbiance (après le geste de déblocage).
 */
export function startMusic(world: WorldKey): void {
  if (musicWorld === world && musicTimer !== null) return
  const c = ac() // crée le contexte + le graphe de bus si besoin (on est après le geste utilisateur)
  const switching = musicWorld !== null
  if (switching) hardStopVoices(c.currentTime + 0.26) // coupe l'ancien biome après son fondu sortant
  if (musicTimer !== null) { clearInterval(musicTimer); musicTimer = null }

  musicWorld = world
  const spec = MUSIC[world]
  musicSend!.gain.setValueAtTime(0.15 + spec.reverb * 0.3, c.currentTime) // espace (réverb) propre au biome
  stepIndex = 0

  const t = c.currentTime, p = musicBus!.gain, target = muted ? 0 : MUSIC_LEVEL
  p.cancelScheduledValues(t); p.setValueAtTime(p.value, t) // repart de la valeur courante (pas de conflit de rampe)
  if (switching) {
    p.linearRampToValueAtTime(0, t + 0.22)      // fondu sortant de l'ancien biome
    p.setValueAtTime(0, t + 0.3)                // trou de garde (anciennes voix coupées à +0.26)
    p.linearRampToValueAtTime(target, t + 1.1)  // fondu entrant du nouveau biome
    nextTime = t + 0.3                          // les nouvelles notes démarrent avec le fondu entrant
  } else {
    p.setValueAtTime(0, t)
    p.linearRampToValueAtTime(target, t + 0.8)  // fondu entrant à froid
    nextTime = t + 0.1
  }
  musicTimer = setInterval(tickScheduler, SCHED_MS)
}

/**
 * Arrête la musique (sortie de partie / changement de niveau). Coupe le scheduler, fait un court
 * fondu sortant (pas de clic), puis ARRÊTE + DÉCONNECTE toutes les sources en vol (zéro fuite).
 * Idempotent. Miroir de stopAmbiance.
 */
export function stopMusic(): void {
  if (musicTimer !== null) { clearInterval(musicTimer); musicTimer = null }
  musicWorld = null
  if (!ctx || !musicBus) { voices.clear(); return }
  const t = ctx.currentTime, p = musicBus.gain
  p.cancelScheduledValues(t); p.setValueAtTime(p.value, t)
  p.linearRampToValueAtTime(0, t + 0.2)  // fondu sortant doux
  hardStopVoices(t + 0.22)               // puis coupe/déconnecte TOUTES les sources (onended les retire du set)
}

// Applique le mute à la musique EN COURS. Référence le `ctx` module-level (JAMAIS ac()) : basculer
// le son au menu, avant tout geste, ne doit rien instancier. Le scheduler saute déjà la création de
// nœuds quand `muted` ; ici on (dé)fait entendre le bus en douceur et on resynchronise à la reprise.
function syncMusicMute(): void {
  if (!ctx || !musicBus || musicWorld === null) return
  const t = ctx.currentTime, p = musicBus.gain
  p.cancelScheduledValues(t); p.setValueAtTime(p.value, t)
  p.linearRampToValueAtTime(muted ? 0 : MUSIC_LEVEL, t + 0.2)
  if (!muted) nextTime = Math.max(nextTime, t) // reprise : recale la phase (pas de rafale de rattrapage)
}

/**
 * Route chaque événement de jeu vers son jingle. digAnim est purement visuel (aucun son).
 *
 * `serieN` (26/07, chantier « jus ») = longueur de la SÉRIE de pièces en cours chez le joueur local.
 * Elle ne sert qu'à la hauteur du blip d'or, qui monte d'un demi-ton par pièce enchaînée : c'est la
 * récompense la plus immédiate du jeu, celle qu'on entend avant même de lire le compteur. 0 ou 1 =
 * la note d'origine, donc les appelants qui l'ignorent (en ligne, la Mine) ne changent pas de son.
 */
/**
 * UN MOINE VIENT DE TE REPÉRER (26/07, chantier « la chasse »). La bulle « ! » existait déjà mais
 * en SILENCE : on ne la voyait que si on regardait le moine, c'est-à-dire jamais quand on fuit.
 * Deux notes sèches qui montent, courtes, reconnaissables entre toutes — c'est l'information la
 * plus utile du jeu : « il t'a vu ». Volontairement discret pour supporter deux ou trois moines.
 */
export function sonRepere(): void {
  seq([
    { f: 520, ms: 55, at: 0, type: 'square', vol: 0.13 },
    { f: 780, ms: 70, at: 0.05, type: 'square', to: 700, vol: 0.11 },
  ])
}

// Mémoire du limiteur de « boing » (06/08) : un ressort sous un plafond bas en tirait onze en cinq
// secondes. La règle vit dans client/cadence.ts, pure et testée ; ici ne reste que son horloge.
const dernierBoing = new Map<number, number>()
const boingAutorise = (runner: number): boolean => cadenceOk(dernierBoing, runner, performance.now())

export function playEvents(events: readonly GameEvent[], serieN = 0): void {
  const orHz = serieHz(Math.max(serieN, 1))
  for (const e of events) {
    switch (e.type) {
      case 'gold':         tone(orHz, 90, 'square', Math.round(orHz * 1.5)); break               // blip carré montant, d'autant plus haut que la série est longue
      case 'dig':          tone(140, 50, 'square', 85, 0.16); noise(70, 1800, 0.13)                 // « tchac » mat du coup de pioche (maquette B, 18/07)
                           noise(50, 500, 0.12, 0.10); noise(40, 400, 0.08, 0.17); break            // + gravats qui retombent
      case 'digSand':      noise(80, 300, 0.22); break                                           // bruit grave
      case 'regrow':       tone(210, 60, 'sine', 140, 0.1); break                                // tic sourd
      case 'death':        seq(deathSound(e.cause)); break                                        // son différencié par cause (Tâche 2.3)
      case 'hazard':       seq([                                                                 // alerte : deux notes graves montantes
        { f: 200, ms: 130, at: 0, type: 'sawtooth', to: 320, vol: 0.22 },
        { f: 320, ms: 160, at: 0.14, type: 'sawtooth', to: 200, vol: 0.22 },
      ]); break
      case 'monkTrapped':  seq([{ f: 160, ms: 90, at: 0 }, { f: 130, ms: 110, at: 0.12 }]); break // double blip grave
      case 'goldDropped':  tone(660, 90); break                                                  // blip 660 Hz
      case 'boom':         boom(); break
      case 'teleport':     teleport(); break
      case 'denied':       toc(); break                                                     // « toc » sourd : geste refusé
      case 'itemPicked':   seq([{ f: 700, ms: 90, at: 0, type: 'triangle' }, { f: 1050, ms: 110, at: 0.1, type: 'triangle' }]); break // deux notes montantes
      case 'dash':         seq([                                                              // SON C « cristal » (maquettes 18/07, choisi par Claude-Alain)
        { f: 620, ms: 120, at: 0, type: 'triangle', to: 1380, vol: 0.14 },                       // glissando : la charge qui monte puis fuse
        { f: 1760, ms: 60, at: 0.07, type: 'sine', vol: 0.07 },                                  // arpège d'éclats
        { f: 2093, ms: 70, at: 0.13, type: 'sine', vol: 0.05 },
      ]); break
      case 'taze':         seq([                                                                 // claquement aigu + grésillement descendant
        { f: 1400, ms: 45, at: 0, type: 'square', to: 700 },
        { f: 280, ms: 130, at: 0.05, type: 'sawtooth', to: 90, vol: 0.18 },
      ]); break
      case 'exitRevealed': seq([                                                                 // arpège 4 notes
        { f: 523, ms: 110, at: 0, type: 'triangle' },
        { f: 659, ms: 110, at: 0.1, type: 'triangle' },
        { f: 784, ms: 110, at: 0.2, type: 'triangle' },
        { f: 1047, ms: 180, at: 0.3, type: 'triangle' },
      ]); break
      case 'win':          seq([                                                                 // fanfare 5 notes ~600 ms
        { f: 523, ms: 100, at: 0 },
        { f: 659, ms: 100, at: 0.1 },
        { f: 784, ms: 100, at: 0.2 },
        { f: 1047, ms: 120, at: 0.3 },
        { f: 1319, ms: 220, at: 0.42 },
      ]); break
      case 'bounce':       if (boingAutorise(e.runner)) tone(300, 120, 'sine', 900, 0.14); break  // « boing » de trampoline (montant), cadence bornée
      // L'EAU (06/08) : deux gestes, deux voix — et surtout PLUS le boing du trampoline.
      // Un plouf, c'est du bruit filtré, pas une note : une gerbe large qui s'éteint vite.
      case 'waterExit':    noise(120, 1400, 0.14); noise(180, 700, 0.10, 0.05); break             // on jaillit : gouttes claires puis retombée
      case 'waterEnter':   noise(90, 500, 0.20); noise(220, 260, 0.13, 0.04); break               // on perce la surface : le « plouf » sourd et sa traîne
      case 'crapaud':      seq([                                                                  // drapeau attrapé : coâ descendant
        { f: 340, ms: 90, at: 0, type: 'square', to: 180, vol: 0.13 },
        { f: 220, ms: 140, at: 0.09, type: 'square', to: 120, vol: 0.12 },
      ]); break
      case 'decrapaud':    tone(240, 110, 'square', 520, 0.12); break                             // pouf inverse : il redevient coureur
      case 'crapaudHop':   tone(320, 70, 'sine', 620, 0.07); break                                // petit boing discret du bond auto
      case 'geant':        seq([                                                                  // la gorgée puis la CROISSANCE (grave qui monte)
        { f: 520, ms: 80, at: 0, type: 'triangle', to: 300, vol: 0.12 },
        { f: 90, ms: 260, at: 0.08, type: 'sawtooth', to: 220, vol: 0.16 },
      ]); break
      case 'degeant':      tone(260, 150, 'square', 90, 0.13); break                              // le « pof » du rétrécissement
      case 'ecrase':       seq([                                                                  // moine APLATI : gros toc mat + petit couic
        { f: 140, ms: 90, at: 0, type: 'square', to: 60, vol: 0.18 },
        { f: 900, ms: 60, at: 0.09, type: 'triangle', to: 1400, vol: 0.08 },
      ]); break
      case 'bouscule':     tone(180, 130, 'sine', 540, 0.13); break                               // l'adversaire expédié en vol plané
      case 'meuteInvoquee': seq([                                                                 // la cloche, puis le grondement des deux spectres
        { f: 880, ms: 90, at: 0, type: 'triangle', to: 660, vol: 0.13 },
        { f: 110, ms: 320, at: 0.09, type: 'sawtooth', to: 70, vol: 0.15 },
        { f: 200, ms: 200, at: 0.2, type: 'square', to: 130, vol: 0.09 },
      ]); break
      case 'spectreParti': tone(300, 180, 'sine', 60, 0.1); break                                 // il s'évapore : soupir descendant
      case 'couronnePrise': seq([                                                                 // fanfare COURTE et inquiétante : l'or et la malédiction
        { f: 523, ms: 90, at: 0, type: 'triangle', vol: 0.13 },
        { f: 784, ms: 90, at: 0.09, type: 'triangle', vol: 0.13 },
        { f: 622, ms: 220, at: 0.18, type: 'sawtooth', to: 300, vol: 0.11 },
      ]); break
      case 'couronneTombee': tone(660, 140, 'triangle', 240, 0.11); break                         // elle roule et attend le prochain
      case 'fantome':      seq([                                                                  // la disparition : souffle qui monte et s'éteint
        { f: 420, ms: 130, at: 0, type: 'sine', to: 1100, vol: 0.1 },
        { f: 1100, ms: 160, at: 0.11, type: 'sine', to: 200, vol: 0.06 },
      ]); break
      case 'defantome':    tone(300, 160, 'sine', 700, 0.08); break                                // la rematérialisation, en douceur
      case 'bandelettePose': tone(600, 80, 'triangle', 300, 0.08); break                          // la bande se tend au sol, discrètement
      case 'momifie':      seq([                                                                  // l'enroulement : froissement qui monte puis se referme
        { f: 240, ms: 120, at: 0, type: 'sawtooth', to: 620, vol: 0.13 },
        { f: 700, ms: 90, at: 0.11, type: 'triangle', to: 240, vol: 0.11 },
      ]); break
      case 'momieCoup':    tone(420, 45, 'square', 620, 0.08); break                              // un coup du débattement a compté
      case 'demomifie':    seq([                                                                  // le cocon ÉCLATE : il est libre
        { f: 300, ms: 60, at: 0, type: 'square', to: 900, vol: 0.13 },
        { f: 900, ms: 110, at: 0.05, type: 'triangle', to: 1500, vol: 0.1 },
      ]); break
      case 'patateAllumee': seq([                                                                 // la mèche s'allume : chuintement montant
        { f: 180, ms: 90, at: 0, type: 'sawtooth', to: 520, vol: 0.12 },
        { f: 900, ms: 160, at: 0.08, type: 'triangle', to: 1300, vol: 0.09 },
      ]); break
      case 'patatePassee': tone(700, 70, 'triangle', 980, 0.11); break                            // « tiens, cadeau ! »
      // L'ÉCHANGE D'OUTIL (06/08, « un son doit être joué lors du changement d'outils ») : deux
      // signatures franches et OPPOSÉES — on doit savoir ce qu'on vient de prendre les yeux sur
      // l'écran. Le maillet : la pioche se plante (toc mat), puis l'acier monte et sonne.
      case 'malletPris':   seq([
        { f: 200, ms: 70, at: 0, type: 'square', to: 90, vol: 0.16 },      // la pioche qui se plante
        { f: 380, ms: 110, at: 0.07, type: 'sawtooth', to: 760, vol: 0.14 }, // le maillet qu'on soulève
        { f: 1150, ms: 200, at: 0.17, type: 'triangle', to: 1500, vol: 0.12 }, // l'acier qui sonne
      ]); break
      case 'malletRendu':  seq([                                                                  // l'inverse : l'acier repose, la pioche revient en main
        { f: 900, ms: 90, at: 0, type: 'triangle', to: 420, vol: 0.12 },
        { f: 300, ms: 130, at: 0.08, type: 'square', to: 620, vol: 0.13 },
      ]); break
      // LE PAS DU PORTEUR (06/08) : un clonk métallique SOURD et discret à chaque case — assez
      // pour sentir le poids sous le pied, assez bas pour ne jamais fatiguer sur une traversée.
      case 'malletPas':    seq([
        { f: 92, ms: 55, at: 0, type: 'square', to: 58, vol: 0.075 },
        { f: 340, ms: 40, at: 0.02, type: 'triangle', to: 190, vol: 0.035 },
      ]); break
      case 'malletSwing':  seq([                                                                  // le woosh du swing : bref, net, on SAIT qu'on a frappé
        { f: 320, ms: 40, at: 0, type: 'triangle', to: 110, vol: 0.13 },
        { f: 150, ms: 60, at: 0.03, type: 'sine', to: 70, vol: 0.1 },
      ]); break
      case 'malletHit':    seq([                                                                  // LE BOING : ressort + impact — la signature de l'objet
        { f: 120, ms: 70, at: 0, type: 'square', to: 60, vol: 0.17 },
        { f: 260, ms: 160, at: 0.05, type: 'sine', to: 900, vol: 0.14 },
      ]); break
      case 'deguise':      seq([                                                                  // le masque s'enfile : froissement de robe, discret
        { f: 700, ms: 50, at: 0, type: 'triangle', to: 420, vol: 0.07 },
        { f: 420, ms: 90, at: 0.05, type: 'triangle', to: 300, vol: 0.06 },
      ]); break
      case 'dedeguise':    seq([                                                                  // le pouf de fumée qui révèle
        { f: 300, ms: 70, at: 0, type: 'square', to: 700, vol: 0.12 },
        { f: 500, ms: 120, at: 0.07, type: 'triangle', to: 220, vol: 0.1 },
      ]); break
      case 'stung':        seq([                                                                  // piqûre de MÉDUSE : bzzz électrique bref
        { f: 1100, ms: 60, at: 0, type: 'square', to: 700, vol: 0.14 },
        { f: 700, ms: 110, at: 0.06, type: 'square', to: 1000, vol: 0.12 },
      ]); break
      case 'fishDied':     tone(320, 140, 'sine', 90, 0.16); break                                // plouf grave : poisson soufflé
      case 'barrier':      seq(e.open                                                         // clang de barriere
        ? [{ f: 300, ms: 70, at: 0, type: 'square', to: 600, vol: 0.14 }, { f: 600, ms: 90, at: 0.07, type: 'square', to: 900, vol: 0.12 }]
        : [{ f: 600, ms: 70, at: 0, type: 'square', to: 300, vol: 0.16 }, { f: 200, ms: 110, at: 0.07, type: 'square', to: 90, vol: 0.16 }]); break
      case 'dashCrystal':  seq([                                                              // ding cristallin : dash recharge
        { f: 1200, ms: 70, at: 0, type: 'triangle', vol: 0.14 },
        { f: 1800, ms: 140, at: 0.07, type: 'triangle', to: 2200, vol: 0.12 },
      ]); break
      case 'boulderRoll':  seq([                                                                  // rocher lancé : grondement sourd
        { f: 90, ms: 220, at: 0, type: 'sawtooth', to: 55, vol: 0.2 },
        { f: 70, ms: 260, at: 0.16, type: 'sawtooth', to: 40, vol: 0.16 },
      ]); break
      case 'gong':         seq([                                                                  // GONG D'OR (17/07) : coup grave + résonance
        { f: 220, ms: 90, at: 0, type: 'triangle' },
        { f: 98, ms: 620, at: 0.03, type: 'sine' },
        { f: 147, ms: 480, at: 0.06, type: 'sine' },
      ]); break
      case 'rebuild':      seq([{ f: 340, ms: 45, at: 0, type: 'square' }, { f: 290, ms: 55, at: 0.11, type: 'square' }]); break // toc-toc de truelle (bâtisseur)
      case 'checkpoint':   seq([                                                                  // réveil du ver : 3 notes joyeuses montantes
        { f: 392, ms: 90, at: 0, type: 'triangle' },
        { f: 523, ms: 90, at: 0.09, type: 'triangle' },
        { f: 784, ms: 160, at: 0.18, type: 'triangle' },
      ]); break
      case 'crumble':      noise(90, 350, 0.16); break                                            // tuile qui s'effrite (bruit grave)
      case 'crateBreak':   noise(70, 1400, 0.22); tone(190, 100, 'square', 70, 0.12); break        // caisse qui éclate : craquement sec + planche qui casse
      case 'keyPicked':    seq([{ f: 1046, ms: 70, at: 0, type: 'triangle' }, { f: 1568, ms: 120, at: 0.07, type: 'triangle' }]); break // ding clair (clé)
      case 'doorOpen':     seq([{ f: 392, ms: 90, at: 0, type: 'sine' }, { f: 523, ms: 90, at: 0.08, type: 'sine' }, { f: 784, ms: 160, at: 0.16, type: 'sine' }]); break // accord d'ouverture
      case 'monkDied':     seq([{ f: 440, ms: 90, at: 0, type: 'sawtooth', to: 220 }, { f: 220, ms: 160, at: 0.09, type: 'sawtooth', to: 90, vol: 0.16 }]); break // moine vaincu (descendant)
      case 'bombPlaced':   tone(150, 55, 'sine', 110, 0.09); break                                // pose de bombe (toc grave)
      case 'ropePlaced':   tone(520, 70, 'triangle', 300, 0.1); break                             // corde tendue (whip court)
      default: break
    }
  }
}

// --- MAQUETTE III « LES TRAPPES » (18/08) : le grain de chaque verbe. Ajouts PURS en fin de
// module, car les primitives tone/noise/seq sont privées ici. Le bruit est déjà une MÉCANIQUE
// (les Veilleurs entendent) : ce qui sonne fort ci-dessous attire aussi fort dans la simulation,
// jamais l'inverse. Tous respectent `muted`. ---

/** La percée : la case tombe d'un coup — impact sourd, sub grave, gravats. Plus brutal qu'une frappe. */
export function trapBreach(): void {
  if (muted) return
  tone(84, 150, 'square', 46, 0.26)
  tone(50, 240, 'sine', 34, 0.30)
  noise(160, 780, 0.22)
}

/** Un cran qui remonte. Le grondement MONTE D'UN TON à chaque cran comblé : on entend la fin
 *  approcher sans regarder — l'oreille compte avec l'œil. `cransRestants` : 2, puis 1… */
export function trapRumble(cransRestants: number): void {
  if (muted) return
  const f = 64 * Math.pow(1.35, Math.max(0, 3 - Math.max(0, cransRestants)))
  tone(f, 190, 'sawtooth', f * 0.82, 0.15)
  noise(150, 480, 0.10)
}

/** Le scellement : le coup sourd de la terre qui se referme. */
export function trapSeal(): void {
  if (muted) return
  tone(150, 60, 'square', 70, 0.20)
  tone(46, 300, 'sine', 30, 0.34)
  noise(230, 620, 0.18)
}

/** La ruade : un talon dans le sol — sec, grave, et ça porte (la sim fait pareil : ça s'entend LOIN). */
export function kickThump(): void {
  if (muted) return
  tone(120, 90, 'square', 55, 0.24)
  tone(42, 260, 'sine', 30, 0.32)
  noise(120, 420, 0.16)
}

/** S'agripper : un frottement de terre, discret — grimper n'attire pas les Veilleurs. */
export function grabScrape(): void {
  if (muted) return
  noise(180, 2400, 0.06)
  tone(320, 120, 'triangle', 480, 0.07)
}

/** Le sifflet : deux notes aiguës qui portent — le son du leurre. */
export function whistleFar(): void {
  if (muted) return
  seq([
    { f: 2100, ms: 120, at: 0, type: 'sine', vol: 0.12 },
    { f: 2800, ms: 220, at: 0.13, type: 'sine', vol: 0.12 },
  ])
}

/** Le limon : un splotch grave — un trou noyé d'un coup. */
export function siltPour(): void {
  if (muted) return
  noise(260, 300, 0.20)
  tone(90, 220, 'sine', 40, 0.20)
}

/** L'étai : deux tocs de bois — du solide, enfin. */
export function bracePlace(): void {
  if (muted) return
  seq([
    { f: 420, ms: 60, at: 0, type: 'square', vol: 0.12 },
    { f: 320, ms: 80, at: 0.09, type: 'square', vol: 0.14 },
  ])
}

/** Le battement de cœur quand c'est TOI qui es dans le trou : toum-toum, grave, insistant. */
export function heartThump(): void {
  if (muted) return
  seq([
    { f: 58, ms: 100, at: 0, type: 'sine', vol: 0.22 },
    { f: 50, ms: 90, at: 0.16, type: 'sine', vol: 0.15 },
  ])
}

// --- MAQUETTE IV « LA RUÉE » (18/08 au soir) : le son MONTE avec le jeu. Le pop de casse se
// pitche sur le combo, l'absorption grimpe en rafale — l'oreille entend le score monter. ---

/** Le pop d'une terre cassée : bref, sec, et PLUS AIGU à chaque palier de combo. */
export function digPop(mult: number): void {
  if (muted) return
  const f = 240 * (1 + 0.18 * Math.max(0, mult - 1))
  tone(f, 60, 'square', f * 0.6, 0.14)
  noise(50, 900, 0.10)
}

/** Le filon crevé : un carillon doré. */
export function veinChime(): void {
  if (muted) return
  seq([
    { f: 1046, ms: 70, at: 0, type: 'triangle', vol: 0.14 },
    { f: 1568, ms: 90, at: 0.07, type: 'triangle', vol: 0.14 },
    { f: 2093, ms: 150, at: 0.15, type: 'triangle', vol: 0.12 },
  ])
}

/** Un coup dans la pierre : clang sourd — le hitstop l'accompagne. */
export function rockKnock(): void {
  if (muted) return
  tone(180, 70, 'square', 90, 0.2)
  tone(60, 140, 'sine', 44, 0.22)
  noise(80, 1400, 0.12)
}

/** La pierre éclate : gravats et sub. */
export function rockShatter(): void {
  if (muted) return
  tone(120, 110, 'square', 55, 0.24)
  tone(48, 240, 'sine', 32, 0.3)
  noise(180, 1100, 0.2)
}

/** Une absorption : un tic clair, plus aigu quand la rafale s'emballe (k = rang dans la rafale). */
export function gemTick(k: number): void {
  if (muted) return
  const f = 900 * Math.pow(1.06, Math.min(16, k))
  tone(f, 45, 'sine', f * 1.2, 0.09)
}

/** Le palier de combo : deux notes qui grimpent. */
export function comboUp(mult: number): void {
  if (muted) return
  const base = 520 * (1 + 0.12 * mult)
  seq([
    { f: base, ms: 70, at: 0, type: 'square', vol: 0.13 },
    { f: base * 1.5, ms: 120, at: 0.07, type: 'square', vol: 0.13 },
  ])
}

/** LA FIÈVRE : la fanfare qui dit « tout est permis ». */
export function feverRush(): void {
  if (muted) return
  seq([
    { f: 523, ms: 90, at: 0, type: 'square', vol: 0.16 },
    { f: 784, ms: 90, at: 0.09, type: 'square', vol: 0.16 },
    { f: 1046, ms: 110, at: 0.18, type: 'square', vol: 0.16 },
    { f: 1568, ms: 260, at: 0.28, type: 'square', vol: 0.15 },
  ])
}

/** La fièvre retombe : une descente douce. */
export function feverDown(): void {
  if (muted) return
  seq([
    { f: 784, ms: 110, at: 0, type: 'triangle', vol: 0.12 },
    { f: 523, ms: 160, at: 0.11, type: 'triangle', vol: 0.11 },
  ])
}

/** Le Rôdeur t'a eu : impact grave, et la poche qui sonne creux. */
export function raiderThud(): void {
  if (muted) return
  tone(90, 120, 'square', 40, 0.26)
  tone(42, 260, 'sine', 30, 0.3)
  noise(160, 500, 0.18)
}

/** Tes éclats giclent : une petite pluie de pertes. */
export function lossScatter(): void {
  if (muted) return
  seq([
    { f: 700, ms: 50, at: 0, type: 'sine', vol: 0.10, to: 500 },
    { f: 600, ms: 50, at: 0.05, type: 'sine', vol: 0.09, to: 420 },
    { f: 520, ms: 60, at: 0.10, type: 'sine', vol: 0.08, to: 360 },
  ])
}

/** Fin de manche : le sifflet de chantier. */
export function finWhistle(): void {
  if (muted) return
  seq([
    { f: 1800, ms: 160, at: 0, type: 'sine', vol: 0.13 },
    { f: 1800, ms: 320, at: 0.2, type: 'sine', vol: 0.13, to: 1200 },
  ])
}

/** La mèche du TNT : le tss-tss qui dit « dégage ». */
export function tntFuse(): void {
  if (muted) return
  noise(90, 5200, 0.06)
  tone(2400, 60, 'sine', 3200, 0.05)
}

/** LE BOUM : le plus gros son du jeu — double sub, souffle long. */
export function tntBoom(): void {
  if (muted) return
  tone(70, 200, 'square', 30, 0.3)
  tone(40, 420, 'sine', 24, 0.36)
  noise(340, 900, 0.26)
  noise(180, 2600, 0.12)
}

/** La géode s'ouvre : un carillon majeur qui ruisselle. */
export function geodeBurst(): void {
  if (muted) return
  seq([
    { f: 1046, ms: 90, at: 0, type: 'triangle', vol: 0.15 },
    { f: 1318, ms: 90, at: 0.08, type: 'triangle', vol: 0.15 },
    { f: 1568, ms: 100, at: 0.16, type: 'triangle', vol: 0.15 },
    { f: 2093, ms: 240, at: 0.26, type: 'triangle', vol: 0.14 },
  ])
}

/** La descente d'étage : un gliss qui plonge, puis le ding des trente secondes gagnées. */
export function stairDown(): void {
  if (muted) return
  tone(600, 260, 'sine', 220, 0.14)
  seq([{ f: 1568, ms: 140, at: 0.28, type: 'sine', vol: 0.11 }])
}

// --- MAQUETTES V-VII « LA TOUR INFINIE » (18/08) : trois sons de plus, ajouts purs. ---

/** UN PALIER TOMBE : la fanfare de déblocage — plus ronde que la fièvre, elle annonce un POUVOIR. */
export function skillUp(): void {
  if (muted) return
  seq([
    { f: 392, ms: 80, at: 0, type: 'square', vol: 0.15 },
    { f: 523, ms: 80, at: 0.08, type: 'square', vol: 0.15 },
    { f: 659, ms: 90, at: 0.16, type: 'square', vol: 0.15 },
    { f: 1046, ms: 300, at: 0.25, type: 'triangle', vol: 0.16 },
  ])
  tone(65, 260, 'sine', 50, 0.2)
}

/** LE FEU T'A EU : un rugissement qui monte puis s'étouffe — la fin de la Fournaise. */
export function fireWhoosh(): void {
  if (muted) return
  noise(520, 700, 0.24)
  tone(90, 480, 'sawtooth', 36, 0.22)
  seq([{ f: 1400, ms: 420, at: 0.1, type: 'sine', vol: 0.07, to: 300 }])
}

/** Une carte d'amélioration se retourne : un clac bref, papier et bois. */
export function cardFlip(): void {
  if (muted) return
  tone(900, 40, 'square', 500, 0.08)
  noise(45, 2400, 0.07)
  seq([{ f: 1300, ms: 60, at: 0.05, type: 'sine', vol: 0.07 }])
}

// --- MAQUETTES VIII-IX « LES NOUVELLES BOULES » (19/08) : deux sons de plus, ajouts purs. ---

/** Le BOING du bumper : le ressort du flipper — élastique, joyeux, jamais pareil deux fois. */
export function bumperBoing(): void {
  if (muted) return
  tone(220, 90, 'sine', 440, 0.16)
  tone(180, 130, 'triangle', 520, 0.12)
  noise(35, 3200, 0.06)
}

/** LE STRIKE : la cloche du champion — trois coups francs qui montent. */
export function strikeBell(): void {
  if (muted) return
  seq([
    { f: 880, ms: 90, at: 0, type: 'triangle', vol: 0.16 },
    { f: 1174, ms: 90, at: 0.09, type: 'triangle', vol: 0.16 },
    { f: 1760, ms: 340, at: 0.18, type: 'triangle', vol: 0.17 },
  ])
  tone(70, 200, 'sine', 50, 0.18)
}

// --- MAQUETTE X « LA FORGE » (24/08) : la note de forge, pitchée en demi-tons. Ajout pur. ---

/** Le clang MUSICAL de l'enclume : k en demi-tons au-dessus de Do3 — la mélodie naît des
 *  frappes (0/4/7/12 = l'arpège majeur des quatre enclumes). */
export function forgeClang(k: number): void {
  if (muted) return
  const f = 262 * Math.pow(2, k / 12)
  tone(f, 160, 'triangle', f * 0.99, 0.17)
  tone(f * 2.01, 90, 'sine', f * 2, 0.07)
  noise(30, 5200, 0.06)
}

// --- MAQUETTES XIII-XV « LES JEUX CALMES » (24/08) : la douceur — ajouts purs. ---

/** LE DING de zone révélée : deux harmoniques cristallines, jamais agressives. */
export function ding(): void {
  if (muted) return
  seq([
    { f: 1568, ms: 120, at: 0, type: 'sine', vol: 0.12 },
    { f: 2093, ms: 300, at: 0.06, type: 'sine', vol: 0.10 },
  ])
}

/** Le frottis de mousse : un chuintement doux et court. */
export function froisse(): void {
  if (muted) return
  noise(70, 1400, 0.05)
}

/** La pose d'un domino : le toc de bois, à peine pitché par k — le rythme de la patience. */
export function tocBois(k: number): void {
  if (muted) return
  const f = 190 + (k % 5) * 14
  tone(f, 45, 'triangle', f * 0.9, 0.10)
  noise(18, 900, 0.05)
}

/** Le clac d'un domino qui tombe : sec, léger, la cascade en fait la musique. */
export function clacDomino(k: number): void {
  if (muted) return
  const f = 320 + (k % 12) * 9
  tone(f, 30, 'square', f * 0.85, 0.07)
  noise(14, 2600, 0.05)
}

/** LE GONG : profond, long, la récompense au passage de la cascade. */
export function gongProfond(): void {
  if (muted) return
  tone(98, 900, 'sine', 96, 0.22)
  tone(147, 700, 'sine', 146, 0.12)
  tone(196, 500, 'triangle', 195, 0.08)
}

/** LE TCHOK de l'emboîtement juste : mat, satisfait, définitif. */
export function tchok(): void {
  if (muted) return
  tone(170, 60, 'triangle', 120, 0.16)
  tone(85, 90, 'sine', 70, 0.12)
  noise(20, 1100, 0.05)
}

/** L'accord final doux : la complétion qui chante (fresque révélée, tiroir fermé). */
export function accordDoux(): void {
  if (muted) return
  seq([
    { f: 523, ms: 260, at: 0, type: 'sine', vol: 0.11 },
    { f: 659, ms: 260, at: 0.02, type: 'sine', vol: 0.10 },
    { f: 784, ms: 320, at: 0.04, type: 'sine', vol: 0.10 },
    { f: 1046, ms: 480, at: 0.14, type: 'sine', vol: 0.09 },
  ])
}
